/*
 * Infinite scroll for collection pages + "come back to the same spot" on Back.
 * - Loads the next batch of products when the shopper nears the bottom.
 * - Before leaving the page, remembers how many batches were loaded and
 *   which product was on screen.
 * - When the shopper comes back (Back button or reload), it reloads those
 *   batches and scrolls to the same product.
 */
(function () {
  if (customElements.get('infinite-scroll')) return;

  var STORAGE_KEY = 'infiniteScrollState';
  var MAX_AGE_MS = 30 * 60 * 1000; // remember the spot for 30 minutes
  var PARALLEL = 4; // batches fetched at once while restoring

  function pageKey() {
    var url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    return url.pathname + url.search;
  }

  function navigationType() {
    try {
      var nav = performance.getEntriesByType('navigation')[0];
      if (nav && nav.type) return nav.type; // 'navigate' | 'reload' | 'back_forward'
    } catch (e) {}
    if (performance.navigation) {
      if (performance.navigation.type === 2) return 'back_forward';
      if (performance.navigation.type === 1) return 'reload';
    }
    return 'navigate';
  }

  function readState() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeState(state) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {}
  }

  function clearState() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  class InfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.nextUrl = this.dataset.nextUrl || null;
      this.sectionId = this.dataset.sectionId;
      this.currentPage = parseInt(this.dataset.currentPage || '1', 10);
      this.totalPages = parseInt(this.dataset.totalPages || '1', 10);
      this.loading = false;
      this.button = this.querySelector('.infinite-scroll__button');
      if (this.button) this.button.addEventListener('click', () => this.loadMore());

      this.onPageHide = () => this.saveState();
      this.onVisibility = () => {
        if (document.visibilityState === 'hidden') this.saveState();
      };
      window.addEventListener('pagehide', this.onPageHide);
      document.addEventListener('visibilitychange', this.onVisibility);

      this.maybeRestore().finally(() => this.startObserver());
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('pagehide', this.onPageHide);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    get grid() {
      return document.getElementById('product-grid');
    }

    startObserver() {
      if (!this.nextUrl || !this.isConnected) return;
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
          },
          { rootMargin: '0px 0px 800px 0px' }
        );
        this.observer.observe(this);
      } else {
        this.classList.add('show-button');
      }
    }

    /* ---------- Remember position ---------- */

    saveState() {
      var grid = this.grid;
      if (!grid || !this.isConnected) return;
      var items = grid.querySelectorAll(':scope > li');
      if (!items.length) return;

      // Find the first product card that is visible on screen.
      var anchorIndex = 0;
      var anchorOffset = 0;
      for (var i = 0; i < items.length; i++) {
        var rect = items[i].getBoundingClientRect();
        if (rect.bottom > 0) {
          anchorIndex = i;
          anchorOffset = rect.top;
          break;
        }
      }

      writeState({
        key: pageKey(),
        page: this.currentPage,
        anchorIndex: anchorIndex,
        anchorOffset: anchorOffset,
        scrollY: window.scrollY,
        time: Date.now(),
      });
    }

    /* ---------- Restore position ---------- */

    async maybeRestore() {
      if (window.__infiniteScrollRestoreDone) return;
      window.__infiniteScrollRestoreDone = true;

      var type = navigationType();
      var state = readState();

      if (type === 'navigate') {
        // Fresh visit (e.g. clicked a menu link): start from the top.
        clearState();
        return;
      }
      if (!state || state.key !== pageKey() || Date.now() - state.time > MAX_AGE_MS) return;

      // We will handle scrolling ourselves so the browser doesn't jump around.
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

      this.classList.add('is-loading');
      var root = document.documentElement;
      root.classList.add('infinite-scroll-restoring');
      // Safety net: never keep the page hidden for more than 5 seconds.
      var safety = setTimeout(() => root.classList.remove('infinite-scroll-restoring'), 5000);
      try {
        var targetPage = Math.min(state.page || 1, this.totalPages);
        if (targetPage > this.currentPage && this.nextUrl) {
          await this.loadPagesUpTo(targetPage);
        }
      } catch (e) {
        console.error('Infinite scroll: could not restore all products', e);
      } finally {
        this.classList.remove('is-loading');
      }

      this.scrollToAnchor(state);
      clearTimeout(safety);
      root.classList.remove('infinite-scroll-restoring');
      // Correct once more after images/fonts settle.
      setTimeout(() => this.scrollToAnchor(state), 300);
      window.addEventListener('load', () => this.scrollToAnchor(state), { once: true });
    }

    scrollToAnchor(state) {
      if (this.userScrolledAfterRestore) return;
      var grid = this.grid;
      var items = grid ? grid.querySelectorAll(':scope > li') : [];
      var item = items[Math.min(state.anchorIndex || 0, items.length - 1)];
      var y;
      if (item) {
        y = item.getBoundingClientRect().top + window.scrollY - (state.anchorOffset || 0);
      } else {
        y = state.scrollY || 0;
      }
      this.programmaticScroll = true;
      window.scrollTo(0, Math.max(0, y));
      requestAnimationFrame(() => {
        this.programmaticScroll = false;
      });
      if (!this.scrollWatch) {
        this.scrollWatch = () => {
          if (!this.programmaticScroll) this.userScrolledAfterRestore = true;
        };
        ['wheel', 'touchmove', 'keydown'].forEach((evt) =>
          window.addEventListener(evt, this.scrollWatch, { once: true, passive: true })
        );
      }
    }

    pageUrl(pageNumber) {
      var url = new URL(this.nextUrl, window.location.origin);
      url.searchParams.set('page', pageNumber);
      url.searchParams.set('section_id', this.sectionId);
      return url.toString();
    }

    async fetchDoc(url) {
      var response = await fetch(url);
      if (!response.ok) throw new Error('HTTP ' + response.status);
      var html = await response.text();
      return new DOMParser().parseFromString(html, 'text/html');
    }

    async loadPagesUpTo(targetPage) {
      var pages = [];
      for (var p = this.currentPage + 1; p <= targetPage; p++) pages.push(p);

      for (var i = 0; i < pages.length; i += PARALLEL) {
        var chunk = pages.slice(i, i + PARALLEL);
        var docs = await Promise.all(chunk.map((p) => this.fetchDoc(this.pageUrl(p))));
        for (var j = 0; j < docs.length; j++) {
          var hasItems = this.appendFromDoc(docs[j], chunk[j]);
          if (!hasItems || !this.nextUrl) {
            if (!this.nextUrl) this.finish();
            return;
          }
        }
      }
    }

    /* ---------- Normal infinite scroll ---------- */

    async loadMore() {
      if (this.loading || !this.nextUrl) return;
      this.loading = true;
      this.classList.add('is-loading');
      this.classList.remove('show-button');
      try {
        var url = new URL(this.nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);
        var nextPage = parseInt(url.searchParams.get('page') || this.currentPage + 1, 10);
        var doc = await this.fetchDoc(url.toString());
        this.appendFromDoc(doc, nextPage);
        if (!this.nextUrl) this.finish();
      } catch (error) {
        console.error('Infinite scroll failed to load more products:', error);
        this.classList.add('show-button');
      } finally {
        this.loading = false;
        this.classList.remove('is-loading');
        if (this.nextUrl && this.observer) {
          this.observer.unobserve(this);
          this.observer.observe(this);
        }
      }
    }

    // Adds one batch of products to the grid. Returns false if the batch was empty.
    appendFromDoc(doc, pageNumber) {
      var grid = this.grid;
      var newItems = doc.querySelectorAll('#product-grid > li');
      if (!grid || newItems.length === 0) {
        this.nextUrl = null;
        return false;
      }
      var added = [];
      newItems.forEach((item) => {
        var node = document.importNode(item, true);
        grid.appendChild(node);
        added.push(node);
      });
      this.revealItems(added, grid);
      this.currentPage = pageNumber;
      var nextEl = doc.querySelector('infinite-scroll');
      this.nextUrl = nextEl && nextEl.dataset.nextUrl ? nextEl.dataset.nextUrl : null;
      return true;
    }

    revealItems(items, grid) {
      if (typeof window.initializeScrollAnimationTrigger === 'function') {
        try {
          window.initializeScrollAnimationTrigger(grid);
          return;
        } catch (e) {}
      }
      items.forEach((item) => {
        [item].concat(Array.from(item.querySelectorAll('.scroll-trigger'))).forEach((el) => {
          el.classList.remove('scroll-trigger', 'animate--slide-in', 'animate--fade-in', 'scroll-trigger--offscreen');
        });
      });
    }

    finish() {
      if (this.observer) this.observer.disconnect();
      if (this.button) this.button.remove();
      var spinner = this.querySelector('.infinite-scroll__spinner');
      if (spinner) spinner.remove();
      var end = this.querySelector('.infinite-scroll__end');
      if (end) end.hidden = false;
    }
  }

  customElements.define('infinite-scroll', InfiniteScroll);
})();
